import { DrawerBody, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import { MeshSelect } from './MeshSelect';
import { UploadContainer } from './UploadContainer';
import { AssignmentActions } from 'packages/dataPipeline/ui/uploads/actions/AssignmentActions';
import { FundingRequestActions } from 'packages/dataPipeline/ui/uploads/actions/FundingRequestActions';
import { DrawerProgressBar } from 'packages/dataPipeline/ui/uploads/DrawerProgressBar';
import { DateAndVersionSelector } from 'packages/dataPipeline/ui/uploads/pipelinePicker/DateAndVersionSelector';
import { UploadContextProvider } from '~/packages/dataPipeline/hooks/UploadContext';
import {
  MeshRunParams,
  RDFV,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

// reusable upload + actions block
const UploadActionsSection = ({
  organizationId,
  meshRunParams,
  closeDrawer,
}: {
  organizationId: string;
  meshRunParams: MeshRunParams;
  closeDrawer: () => void;
}) => (
  <UploadContextProvider
    organizationId={organizationId}
    meshRunId={null}
    meshRunParams={meshRunParams}
  >
    <UploadContainer
      organizationId={organizationId}
      closeDrawer={closeDrawer}
    />
    <AssignmentActions />
    <FundingRequestActions />
  </UploadContextProvider>
);

// reusable progress bar block
const UploadProgressSection = ({
  organizationId,
  meshRunParams,
}: {
  organizationId: string;
  meshRunParams: MeshRunParams;
}) => (
  <UploadContextProvider
    organizationId={organizationId}
    meshRunId={null}
    meshRunParams={meshRunParams}
  >
    <DrawerProgressBar />
  </UploadContextProvider>
);

/**
 * Upload for a new pipeline run
 * This component allows the user to select a mesh document and RDFV,
 * and then upload data against the selected mesh.
 * */
export const UploadForNewPipeline = ({
  organizationId,
  rdfv,
  latestMeshDocuments,
  onClose,
}: {
  organizationId: string;
  rdfv: RDFV | null;
  latestMeshDocuments: DataPipeline.MeshDocumentMetadata[];
  onClose: () => void;
}) => {
  // upload drawer lets you choose mesh to upload against
  // when selectedMesh is provided, we restrict the choice to that mesh
  const [
    selectedMeshDocument,
    setSelectedMeshDocument,
  ] = useState<DataPipeline.MeshDocumentMetadata | null>(
    latestMeshDocuments.length ? latestMeshDocuments[0] : null,
  );
  const [selectedRDFV, setSelectedRDFV] = useState<RDFV | null>(rdfv ?? null);

  useEffect(() => {
    // reset rdfv if selectedMeshDocument changes
    // unless we are given rdfv as prop
    if (!rdfv) {
      setSelectedRDFV(null);
    }
  }, [rdfv, selectedMeshDocument]);

  const selectedMeshParams: MeshRunParams | null =
    selectedMeshDocument && selectedRDFV
      ? {
          meshDocumentMetadata: selectedMeshDocument,
          rdfv: selectedRDFV,
        }
      : null;

  // you cannot change mesh once date is entered.
  const allowMeshSelection = !selectedRDFV;
  return (
    <>
      <DrawerBody>
        <VStack spacing="20px" alignItems="flex-start">
          <>
            {/* select credit facility/mesh */}
            <MeshSelect
              allowSelection={allowMeshSelection}
              selectedMeshDocumentId={
                selectedMeshDocument?.meshDocumentId ?? null
              }
              onMeshSelected={setSelectedMeshDocument}
              meshDocuments={latestMeshDocuments}
            />
            {selectedMeshDocument && (
              <DateAndVersionSelector
                // you can't change date and version once selected
                // to reset the selection, you need to click "New Report"
                allowSelection={!selectedRDFV}
                borrowerOrganizationId={
                  selectedMeshDocument.borrowerOrganizationId
                }
                selectedRDFV={selectedRDFV}
                setSelectedRDFV={setSelectedRDFV}
              />
            )}

            {selectedMeshParams && (
              // show upload tab only when we have selected mesh params
              <UploadActionsSection
                organizationId={organizationId}
                meshRunParams={selectedMeshParams}
                closeDrawer={onClose}
              />
            )}
          </>
        </VStack>
      </DrawerBody>
      {selectedMeshParams && (
        // show progress bar only when upload tab is active
        <UploadProgressSection
          organizationId={organizationId}
          meshRunParams={selectedMeshParams}
        />
      )}
    </>
  );
};

/**
 * Upload for an existing pipeline run
 * This component allows the user to upload data against an existing mesh run.
 * The mesh and RDFV are already selected based on the selectedMeshRun.
 * */
export const UploadForExistingPipeline = ({
  organizationId,
  selectedMeshRun,
  onClose,
}: {
  organizationId: string;
  selectedMeshRun: SelectedMeshRun;
  onClose: () => void;
}) => {
  const { meshRunParams } = selectedMeshRun;
  const { meshDocumentMetadata, rdfv } = meshRunParams;
  return (
    <>
      <DrawerBody>
        <VStack spacing="20px" alignItems="flex-start">
          <>
            {/* select credit facility/mesh */}
            <MeshSelect
              allowSelection={false}
              selectedMeshDocumentId={meshDocumentMetadata.meshDocumentId}
              onMeshSelected={() => {
                // do nothing, mesh is already selected
              }}
              meshDocuments={[meshDocumentMetadata]}
            />
            <DateAndVersionSelector
              // you can't change date and version once selected
              // to reset the selection, you need to click "New Report"
              allowSelection={false}
              borrowerOrganizationId={
                meshDocumentMetadata.borrowerOrganizationId
              }
              selectedRDFV={rdfv}
              setSelectedRDFV={() => {
                // do nothing, date and version are already selected
              }}
            />
            <UploadActionsSection
              organizationId={organizationId}
              meshRunParams={meshRunParams}
              closeDrawer={onClose}
            />
          </>
        </VStack>
      </DrawerBody>
      <UploadProgressSection
        organizationId={organizationId}
        meshRunParams={meshRunParams}
      />
    </>
  );
};
